import React, { useEffect, useState } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Typography,
  Tab,
  Tabs,
} from "@mui/material";
import { getDocs, collection } from "firebase/firestore";
import { db } from "../firebaseConfig.js";
import { formatTranslations, familyTranslations } from "./traductions.js";

const categoryOrder = {
  families: [
    "Cervezas",
    "Sangrías",
    "Vinos",
    "Aperitivos",
    "Cocktails",
    "Gin",
    "Ron",
    "Vodka",
    "Whisky",
    "Anís",
    "Tequila",
    "Licor",
    "Refrescos",
  ],
};
const BeverageProducts = ({ language = "es" }) => {
  const [beverages, setBeverages] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState("Cervezas");

  useEffect(() => {
    const fetchBeverages = async () => {
      const querySnapshot = await getDocs(collection(db, "bebidas-cami"));
      const bebidasData = querySnapshot.docs.map((doc) => doc.data());
      setBeverages(bebidasData);
    };

    fetchBeverages();
  }, []);

  // Obtener los productos filtrados según la familia seleccionada
  const filteredProducts =
    selectedFamily === "Todo"
      ? beverages
      : beverages.filter((product) => product.familia === selectedFamily);

  // Definir el orden predeterminado de las familias
  const familyOrder = [
    "Cervezas",
    "Sangrías",
    "Vinos",
    "Aperitivos",
    "Cocktails",
    "Gin",
    "Ron",
    "Vodka",
    "Whisky",
    "Anís",
    "Tequila",
    "Licor",
  ]; // Aquí puedes agregar más familias si es necesario

  // Agrupar productos por familia o subfamilia según corresponda
  const filteredGroupedProducts = filteredProducts.reduce((acc, product) => {
    const family = product.familia;
    const subfam = product.subfam || "Sin Clasificar"; // Manejar productos sin subfamilia

    // Si la vista es "Vinos", agrupamos por subfamilia
    if (selectedFamily === "Vinos") {
      acc[subfam] = acc[subfam] || [];
      acc[subfam].push(product);
    } else {
      acc[family] = acc[family] || [];
      acc[family].push(product);
    }
    return acc;
  }, {});

  // Si la vista es "Todo", queremos ordenar las familias en el orden predeterminado
  let orderedProducts = {};
  if (selectedFamily === "Todo") {
    // Asegurarse de que las familias estén en el orden deseado
    orderedProducts = familyOrder.reduce((acc, family) => {
      if (filteredGroupedProducts[family]) {
        // Ordenar los productos dentro de la familia según "ordenExcel"
        const sortedFamilyProducts = filteredGroupedProducts[family].sort(
          (a, b) => a.ordenExcel - b.ordenExcel
        );
        acc[family] = sortedFamilyProducts;
      }
      return acc;
    }, {});

    // Si hay familias adicionales no incluidas en familyOrder, se agregan al final
    Object.keys(filteredGroupedProducts).forEach((family) => {
      if (!orderedProducts[family]) {
        // Ordenar las familias no incluidas en familyOrder
        const sortedFamilyProducts = filteredGroupedProducts[family].sort(
          (a, b) => a.ordenExcel - b.ordenExcel
        );
        orderedProducts[family] = sortedFamilyProducts;
      }
    });
  } else {
    // Si no es "Todo", usamos el agrupamiento original sin ordenación de familias
    orderedProducts = filteredGroupedProducts;
  }

  // Aquí tienes "orderedProducts" que contiene los productos ordenados por "ordenExcel"

  return (
    <>
      <Tabs
        value={selectedFamily}
        onChange={(e, newValue) => setSelectedFamily(newValue)}
        variant="scrollable"
        sx={{
          overflowX: "auto",
          margin: { xs: "auto", xl: "auto" },

          top: 47,

          zIndex: 999,
          position: "sticky",
          background: "#f2f2f2",
          display: "flex",
          boxShadow: "0.5px 0.5px 3px #000",
        }}
      >
        {categoryOrder.families.map((family) => (
          <Tab
            key={family}
            value={family}
            label={familyTranslations[language]?.[family] || family}
          />
        ))}
      </Tabs>

      <Container>
        <Grid container spacing={3} sx={{ marginTop: "10px" }}>
          {Object.entries(filteredGroupedProducts).map(([group, products]) => (
            <React.Fragment key={group}>
              {/* Mostrar el título del grupo */}
              <Grid item xs={12}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: "bold", marginTop: "20px" }}
                >
                  {familyTranslations[language]?.[group] || group}
                </Typography>
                <Divider />
              </Grid>

              {/* Mostrar los productos del grupo */}
              {products.map((product) => (
                <Grid item xs={6} sm={4} md={3} key={product.codigo}>
                  <Card
                    sx={{
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      borderRadius: "10px",
                      transition: "transform 0.3s",
                      "&:hover": { transform: "scale(1.05)" },
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="150"
                      image={product.imagenUrl || "images/default-drinks.png"}
                      alt={product.translations?.[language]?.name || "Producto"}
                      sx={{
                        objectFit: "cover",
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                      }}
                    />
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {product.translations?.[language]?.name ||
                          product.producto}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {product.translations?.[language]?.description || ""}
                      </Typography>
                      {product.formatos?.map((formato, index) => (
                        <Typography key={index} variant="body2">
                          {formatTranslations[language]?.[formato.formato] ||
                            formato.formato}{" "}
                          <span style={{ color: "grey", fontStyle: "italic" }}>
                            {formato.cantidad}
                          </span>{" "}
                          - ${formato.precio.toFixed(2)}
                        </Typography>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </React.Fragment>
          ))}
        </Grid>
      </Container>
    </>
  );
};

export default BeverageProducts;
